import axios from 'axios';
import {
	ENDPOINT_URL,
	GET_SSID_TEMPLATE_API,
} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	optionalText,
	resolveCandidatesWithFallback,
	responseRows,
} from '../../core/resolutionUtils';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';
import {resolveSecurityTypeLabel} from '../../../pages/SSID/ssidUtils';

/**
 * Return an SSID Template row identifier.
 * @param {object} item Canvas Template row
 * @return {*} identifier
 */
function templateId(item) {
	return item.ap_ssid_id ?? item.id;
}

/**
 * Normalize a Canvas SSID Template row.
 * @param {object} item Canvas Template row
 * @return {object|null} agent candidate
 */
function normalizeTemplate(item) {
	const id = templateId(item);
	const name = item.ssid_name ?? item.profile_name;
	if (id == null || !name) return null;
	return {
		entity_type: 'template',
		entity_id: String(id),
		name: String(name),
		site_id: null,
		site_name: null,
		security_type: optionalText(
			item.auth_type_label ?? resolveSecurityTypeLabel(item.auth_type),
		),
		status: optionalText(item.status),
		serial_no: null,
	};
}

/**
 * Resolve SSID Template candidates.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveSsidTemplates(params) {
	const query = String(params.query || '').trim();
	// `browse` lists the scope instead of matching a name, so a miss can answer
	// with the SSID Templates that do exist. Named lookups are unchanged.
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	const loadRows = async (search, limit) => {
		const searchParam = search
			? `&search=${encodeURIComponent(search)}`
			: '';
		const url = `${ENDPOINT_URL}${GET_SSID_TEMPLATE_API}?limit=${limit}` +
			`&offset=0${searchParam}`;
		const {data} = await axios.get(url);
		return responseRows(decodeAgentResponse(data) || {});
	};
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		const rows = await loadRows(null, limit);
		replaceResolvedEntities('template', rows, templateId);
		return {
			candidates: rows.map(normalizeTemplate).filter(Boolean).slice(0, limit),
		};
	}
	const {rows, candidates} = await resolveCandidatesWithFallback({
		query,
		loadRows,
		normalizeRow: normalizeTemplate,
		fuzzy: params.fuzzy === true,
	});
	replaceResolvedEntities('template', rows, templateId);
	return {candidates};
}

/**
 * Return a retained SSID Template row.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedSsidTemplate(entityId) {
	return getResolvedEntity('template', entityId);
}
